import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../admin/firebase/config';

/**
 * Hook to fetch menu items for a specific outlet
 * NEW STRUCTURE: outlets/{outletId}/menu_items/{itemId}
 *
 * @param {string} outletId - The outlet ID to filter menu items
 * @returns {object} - { menuItems, menuData, loading }
 */
const useOutletMenuItems = (outletId) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!outletId) {
      console.warn('⚠️ No outlet ID provided to useOutletMenuItems');
      setLoading(false);
      return;
    }

    console.log(`🍽️ Loading menu items for outlet: ${outletId}`);

    // Query global menu_items collection and filter by outlet
    // Path: menu_items where outlets array contains outletId
    const menuItemsRef = collection(db, 'menu_items');
    const q = query(
      menuItemsRef,
      where('available', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Filter items that belong to this outlet
      const items = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(item => {
          // Check if item has outlets array that includes this outletId
          // OR if it has no outlets array (available to all)
          if (item.outlets && Array.isArray(item.outlets)) {
            return item.outlets.includes(outletId);
          }
          // If no outlets field, assume available to all outlets
          return true;
        });

      console.log(`📊 Loaded ${items.length} menu items for outlet ${outletId}`);
      setMenuItems(items);
      setLoading(false);
    }, (error) => {
      console.error('❌ Error fetching menu items:', error);

      // Fallback: try without filters if query fails
      console.warn('⚠️ Trying fallback query without filters');

      const fallbackRef = collection(db, 'menu_items');
      const unsubscribeFallback = onSnapshot(fallbackRef, (snapshot) => {
        const items = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(item => item.available !== false);

        console.log(`📊 Loaded ${items.length} menu items (fallback)`);
        setMenuItems(items);
        setLoading(false);
      }, (fallbackError) => {
        console.error('❌ Fallback query also failed:', fallbackError);
        setMenuItems([]);
        setLoading(false);
      });

      return () => unsubscribeFallback();
    });

    return () => unsubscribe();
  }, [outletId]);

  // Group menu items by category
  const groupedMenu = menuItems.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';

    if (!acc[category]) {
      acc[category] = {
        category: category,
        sections: [{
          name: category,
          subsections: [{
            veg: { items: [] },
            nonVeg: { items: [] }
          }]
        }]
      };
    }

    // Add item to appropriate veg/non-veg array
    const subsection = acc[category].sections[0].subsections[0];

    // Helper to get price from sizes array
    const getPrice = (sizes, index = 0) => {
      return sizes && sizes.length > index && sizes[index] && typeof sizes[index].price === 'number'
        ? sizes[index].price
        : 0;
    };

    const itemData = {
      id: item.id,
      name: item.name || 'Unnamed Item',
      price: item.price || getPrice(item.sizes, 0),
      mrp: item.mrp || getPrice(item.sizes, 1) || getPrice(item.sizes, 0),
      description: item.description || '',
      image: item.imageUrl || item.image || null,
      sizes: item.sizes || [],
      outletId: item.outletId,
      outlets: item.outlets,
      category: item.category
    };

    // Check multiple possible field names for veg/non-veg
    const isVegetarian = item.isVeg || item.veg || item.vegetarian || false;

    if (isVegetarian) {
      subsection.veg.items.push(itemData);
    } else {
      subsection.nonVeg.items.push(itemData);
    }

    return acc;
  }, {});

  // Convert to array format
  const categories = Object.values(groupedMenu);

  return {
    menuItems,
    menuData: {
      outletId: outletId,
      totalItems: menuItems.length,
      categories: categories
    },
    loading
  };
};

export default useOutletMenuItems;
